import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useConfig, waLink, waDisplay, realPhone, realEmail, realAddress, realMaps } from "@/context/ConfigContext";
import { useSeo } from "@/lib/useSeo";
import { usePageCms } from "@/lib/usePageCms";
import { PageHero, SectionHeading } from "@/components/site/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Phone, Mail, MapPin, Clock, MessageCircle, Handshake, Plane, FileText, HelpCircle } from "lucide-react";

const CATEGORIES = ["Partner Relations", "Travel Queries", "Contracting and Supplier Relations", "General Assistance"];

export default function Contact() {
  const CFB = { overline: "Get In Touch", title: "Contact Maha Sampada", subtitle: "Reach the right team for partnerships, travel queries, contracting or general assistance.", image: "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", seo_title: "Contact Maha Sampada | Maharashtra DMC", seo_description: "Get in touch with Maha Sampada for travel queries, partner relations, contracting and general assistance." };
  const { hero, sections } = usePageCms("contact", CFB);
  useSeo({ title: hero.seo_title || CFB.seo_title, description: hero.seo_description || CFB.seo_description });
  const config = useConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ name: "", email: "", mobile: "", category: "General Assistance", message: "", consent: false });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const catS = (sections || []).find((s) => s.kind === "contact_categories");
  const bizS = (sections || []).find((s) => s.kind === "business_details");
  const mapS = (sections || []).find((s) => s.kind === "map");
  const formIntroS = (sections || []).find((s) => s.section_id === "contact_form_intro");
  const CAT_ICONS = { handshake: Handshake, plane: Plane, "file-text": FileText, "help-circle": HelpCircle };
  const contactCards = ((catS && catS.items) || [
    { label: "Partner Relations", email_key: "email_partners", icon: "handshake" },
    { label: "Travel Queries", email_key: "email_queries", icon: "plane" },
    { label: "Contracting & Supplier Relations", email_key: "email_contracting", icon: "file-text" },
    { label: "General Assistance", email_key: "email_general", icon: "help-circle" },
  ]).map((c) => ({ ...c, email: config[c.email_key] })).filter((c) => realEmail(c.email));

  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Enter a valid email address";
    if (!form.message.trim()) e.message = "Please enter a message";
    if (!form.consent) e.consent = "Please provide consent";
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted fields");
      setTimeout(() => document.querySelector('[data-testid$="-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent");
      navigate("/thank-you?type=contact");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send message");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero overline={hero.overline} title={hero.title} subtitle={hero.subtitle}
        image={hero.image} crumbs={[{ label: "Contact" }]} height="h-[46vh]" />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        {catS && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {contactCards.map((c) => { const Icon = CAT_ICONS[c.icon] || HelpCircle; return (
            <div key={c.id || c.label} data-testid="contact-category" className="rounded-2xl border border-gold/25 bg-white p-6">
              <Icon className="h-6 w-6 text-saffron" />
              <h3 className="mt-3 font-display text-lg font-semibold text-charcoal">{c.label}</h3>
              <a href={`mailto:${c.email}`} className="mt-1 block text-sm text-muted-foreground hover:text-saffron">{c.email}</a>
            </div>
          ); })}
        </div>
        )}

        <div className="mt-12 grid gap-10 lg:grid-cols-3">
          {/* Info */}
          <div className="lg:col-span-1">
            {bizS && <SectionHeading overline={bizS.overline || "Reach Us"} title={bizS.heading || "Business Details"} />}
            {bizS && <ul className="mt-6 space-y-4 text-sm">
              {realAddress(config.address) && <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-saffron" /><span className="text-charcoal">{config.address}</span></li>}
              {realPhone(config.phone) && <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-saffron" /><span className="text-charcoal">{config.phone}</span></li>}
              {waLink(config.whatsapp) && <li className="flex items-center gap-3"><MessageCircle className="h-5 w-5 text-saffron" /><a href={waLink(config.whatsapp)} target="_blank" rel="noopener noreferrer" className="text-charcoal hover:text-saffron">{waDisplay(config.whatsapp)} (WhatsApp)</a></li>}
              {realEmail(config.email_general) && <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-saffron" /><span className="text-charcoal">{config.email_general}</span></li>}
              {config.business_hours && <li className="flex items-start gap-3"><Clock className="mt-0.5 h-5 w-5 text-saffron" /><span className="text-charcoal">{config.business_hours}</span></li>}
              {!realAddress(config.address) && !realPhone(config.phone) && !realEmail(config.email_general) && !waLink(config.whatsapp) && (
                <li className="text-muted-foreground">Contact details will be published soon. Please use the form to reach us.</li>
              )}
            </ul>}
            {mapS && realMaps(config.maps_link) && (
              <div className="mt-6 overflow-hidden rounded-2xl border border-gold/25">
                {mapS.heading && <h4 className="mb-2 font-accent text-xs font-semibold uppercase text-maroon">{mapS.heading}</h4>}
                <iframe title="Maha Sampada location" data-testid="maps-embed" className="h-56 w-full grayscale"
                  src={config.maps_link.includes("output=embed") ? config.maps_link : `${config.maps_link}${config.maps_link.includes("?") ? "&" : "?"}output=embed`} loading="lazy" />
              </div>
            )}
          </div>

          {/* Form (PROTECTED — fields & CRM mapping unchanged; only heading is CMS-driven) */}
          <div className="lg:col-span-2">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-gold/25 bg-white p-6 sm:p-10">
              <h2 className="font-display text-2xl font-semibold text-charcoal">{formIntroS?.heading || "Send Us a Message"}</h2>
              {formIntroS?.body && <p className="mt-1 text-sm text-muted-foreground">{formIntroS.body}</p>}
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold text-charcoal">Name *</Label><Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} />{errors.name && <p className="text-xs text-destructive" data-testid="name-error">{errors.name}</p>}</div>
                <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold text-charcoal">Email *</Label><Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />{errors.email && <p className="text-xs text-destructive" data-testid="email-error">{errors.email}</p>}</div>
                <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold text-charcoal">Mobile</Label><Input data-testid="input-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold text-charcoal">Category</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-5 space-y-1.5"><Label className="font-accent text-xs font-semibold text-charcoal">Message *</Label><Textarea data-testid="input-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} />{errors.message && <p className="text-xs text-destructive" data-testid="message-error">{errors.message}</p>}</div>
              <label className="mt-5 flex items-start gap-3"><Checkbox data-testid="checkbox-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" /><span className="text-sm text-charcoal">I consent to Maha Sampada contacting me regarding this message. *</span></label>
              {errors.consent && <p className="text-xs text-destructive" data-testid="consent-error">{errors.consent}</p>}
              <Button type="submit" data-testid="submit-contact" disabled={loading} className="mt-8 rounded-full bg-saffron px-10 font-accent font-semibold text-white hover:bg-saffron-dark">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</> : "Send Your Query"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
